import React, { useState, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";

import {
  Avatar,
  Button,
  CssBaseline,
  TextField,
  Box,
  Typography,
  Container,
  InputAdornment,
  IconButton,
  Link,
} from '@material-ui/core';

import { LockOutlined, Visibility, VisibilityOff } from '@material-ui/icons';

import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";

import { AuthContext } from "../../context/Auth/AuthContext";

// const Copyright = () => {
// 	return (
// 		<Typography variant="body2" color="textSecondary" align="center">
// 			{"Copyleft "}
// 			<Link color="inherit" href="https://github.com/canove">
// 				Canove
// 			</Link>{" "}
// 			{new Date().getFullYear()}
// 			{"."}
// 		</Typography>
// 	);
// };

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
  },
  leftPanel: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
    backgroundColor: "#ffffff",
    position: "relative",
    minHeight: "100vh",
    [theme.breakpoints.up("md")]: {
      flex: "0 0 50%",
      maxWidth: "50%",
      minHeight: "auto",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  rightPanel: {
    flex: 1,
    backgroundImage: "linear-gradient(135deg, rgba(25, 118, 210, 0.85) 0%, rgba(21, 101, 192, 0.9) 100%), url('https://images.unsplash.com/photo-1562774053-701939374585?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: "#ffffff",
    position: "relative",
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },
  loginContainer: {
    width: "100%",
    maxWidth: "400px",
    padding: theme.spacing(3),
    animation: "$fadeInUp 0.8s ease-out",
  },
  "@keyframes fadeInUp": {
    "0%": {
      opacity: 0,
      transform: "translateY(30px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
  logoContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: theme.spacing(4),
  },
  logo: {
    width: "80px",
    height: "80px",
    backgroundColor: "#1976d2",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontSize: "28px",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    boxShadow: "0 4px 20px rgba(25, 118, 210, 0.3)",
  },
  title: {
    color: "#1976d2",
    fontWeight: "700",
    fontSize: "28px",
    marginBottom: theme.spacing(1),
    textAlign: "center",
  },
  subtitle: {
    color: "#666666",
    fontSize: "16px",
    textAlign: "center",
    marginBottom: theme.spacing(1),
  },
  institution: {
    color: "#999999",
    fontSize: "14px",
    textAlign: "center",
    marginBottom: theme.spacing(4),
  },
  form: {
    width: "100%",
  },
  textField: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f8f9fa",
      "&:hover": {
        backgroundColor: "#f1f3f4",
      },
      "&.Mui-focused": {
        backgroundColor: "#ffffff",
        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
      },
    },
    "& .MuiInputLabel-root": {
      color: "#666666",
      "&.Mui-focused": {
        color: "#1976d2",
      },
    },
  },
  submitButton: {
    marginTop: theme.spacing(3),
    backgroundColor: "#1976d2",
    color: "#ffffff",
    fontWeight: "600",
    fontSize: "16px",
    padding: "14px",
    borderRadius: "12px",
    textTransform: "none",
    boxShadow: "0 4px 20px rgba(25, 118, 210, 0.3)",
    "&:hover": {
      backgroundColor: "#1565c0",
      boxShadow: "0 6px 25px rgba(25, 118, 210, 0.4)",
      transform: "translateY(-2px)",
    },
    transition: "all 0.3s ease",
  },
  rightPanelContent: {
    textAlign: "center",
    padding: theme.spacing(4),
    maxWidth: "500px",
  },
  rightPanelTitle: {
    fontSize: "36px",
    fontWeight: "700",
    marginBottom: theme.spacing(2),
    textShadow: "0 2px 4px rgba(0,0,0,0.3)",
  },
  rightPanelSubtitle: {
    fontSize: "18px",
    marginBottom: theme.spacing(3),
    opacity: 0.9,
    lineHeight: 1.6,
  },
  rightPanelFeatures: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  featureItem: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    fontSize: "16px",
    "&::before": {
      content: "'✓'",
      backgroundColor: "#4caf50",
      color: "white",
      borderRadius: "50%",
      width: "24px",
      height: "24px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: theme.spacing(2),
      fontSize: "14px",
      fontWeight: "bold",
    },
  },
  footer: {
    position: "absolute",
    bottom: theme.spacing(2),
    left: "50%",
    transform: "translateX(-50%)",
    textAlign: "center",
    color: "#999999",
    fontSize: "12px",
  },
}));

const Login = () => {
  const classes = useStyles();

  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  const { handleLogin } = useContext(AuthContext);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  return (
    <div className={classes.root}>
      <CssBaseline />
      
      {/* Panel Izquierdo - Formulario */}
      <div className={classes.leftPanel}>
        <div className={classes.loginContainer}>
          <div className={classes.logoContainer}>
            <div className={classes.logo}>
              UTS
            </div>
            <Typography component="h1" className={classes.title}>
              UTS - Entradas
            </Typography>
            <Typography className={classes.subtitle}>
              Sistema de Gestión de Entradas
            </Typography>
            <Typography className={classes.institution}>
              Unidades Tecnológicas de Santander
            </Typography>
          </div>

          <form className={classes.form} noValidate onSubmit={handlSubmit}>
            <TextField
              variant="outlined"
              required
              fullWidth
              id="email"
              label={i18n.t("login.form.email")}
              name="email"
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              autoFocus
              className={classes.textField}
            />
            <TextField
              variant="outlined"
              required
              fullWidth
              name="password"
              label={i18n.t("login.form.password")}
              id="password"
              value={user.password}
              onChange={handleChangeInput}
              autoComplete="current-password"
              type={showPassword ? 'text' : 'password'}
              className={classes.textField}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((e) => !e)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              className={classes.submitButton}
            >
              {i18n.t("login.buttons.submit")}
            </Button>
            <Box textAlign="center" mt={2}>
              <Link
                component={RouterLink}
                to="/forgot-password"
                style={{ color: "#1976d2", textDecoration: "none", fontSize: "14px" }}
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </Box>
          </form>
        </div>

        {/* Footer */}
        <div className={classes.footer}>
          <Typography variant="body2">
            © 2025 Unidades Tecnológicas de Santander
          </Typography>
        </div>
      </div>

      {/* Panel Derecho - Imagen y Contenido */}
      <div className={classes.rightPanel}>
        <div className={classes.rightPanelContent}>
          <Typography className={classes.rightPanelTitle}>
            Bienvenido a UTS
          </Typography>
          <Typography className={classes.rightPanelSubtitle}>
            Sistema de atención de entradas por WhatsApp multiagente
          </Typography>
          <ul className={classes.rightPanelFeatures}>
            <li className={classes.featureItem}>
              Atención multiagente por WhatsApp
            </li>
            <li className={classes.featureItem}>
              Gestión centralizada de conversaciones
            </li>
            <li className={classes.featureItem}>
              Distribución automática de entradas
            </li>
            <li className={classes.featureItem}>
              Historial completo de mensajes
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Login;
